import React, { useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Typography } from '@mui/material';
import type { MuiTelInputCountry } from 'mui-tel-input';

import { UserIdentifier } from '@/api/generated';
import DeleteIcon from '@/assets/icons/delete.svg?react';
import PlusCircleIcon from '@/assets/icons/plus-circle.svg?react';
import DropdownSelect from '@/components/fields/DropdownSelect';
import Field from '@/components/fields/Field';
import typography from '@/theme/typography';
import InviteUserRow from '@/views/fields/InviteUserRow';
import { getClientUserEmailDropdownSelect } from '@/views/fields/UserDropdownSelect/ClientUserEmailDropdownSelect';

import { getRolesList } from './options';

interface UserAccessTableProps {
  name: string;
  organizationId?: string;
  enforceUniqueUsers?: boolean;
  defaultCountry?: MuiTelInputCountry;
}

interface Assignment {
  role?: string;
  user?: UserIdentifier;
  phone?: string;
}

const UserAccessTable: React.FC<UserAccessTableProps> = ({
  name,
  organizationId,
  enforceUniqueUsers,
  defaultCountry
}) => {
  const { t } = useTranslation(['organizations', 'users']);

  const { control } = useFormContext();
  const {
    fields: assignments,
    append: onAddAssignment,
    remove: onRemoveAssignment
  } = useFieldArray({
    control,
    name: name
  });

  const userList = useWatch({
    control,
    name: name
  });

  const omittedUserIds = useMemo(() => {
    if (!enforceUniqueUsers || !userList) return [];

    const getUserIds = (assignments: Assignment[]) =>
      assignments?.map(assignment => assignment?.user?.id || assignment?.user?.email).filter(Boolean) as string[];

    return getUserIds(userList);
  }, [userList, enforceUniqueUsers]);

  const clientUserDropdownSelect = useMemo(
    () =>
      getClientUserEmailDropdownSelect({
        organizationId,
        disabledOptions: omittedUserIds
      }) as React.ComponentType<Record<string, unknown>>,
    [organizationId, omittedUserIds]
  );

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '0.3fr 1fr 32px', width: '100%', gap: 0.5, alignItems: 'start' }}>
      <Typography variant="body3" fontWeight={typography.fontWeightMedium} color="grey.700">
        {t('organizations:layouts.organizations.create.rbac.role')}
      </Typography>
      <Typography variant="body3" fontWeight={typography.fontWeightMedium} color="grey.700">
        {t('organizations:layouts.organizations.create.rbac.user')}
      </Typography>
      <Box />
      {assignments.map((_field, index: number) => (
        <React.Fragment key={_field.id}>
          <Field
            component={DropdownSelect}
            name={`${name}.${index}.role`}
            innerProps={{
              options: getRolesList(t),
              isMulti: false,
              usePortal: true
            }}
            outerSx={{ p: 0, m: 0 }}
          />
          <InviteUserRow
            name={`${name}.${index}`}
            userDropdownSelect={clientUserDropdownSelect}
            placeholder={t('organizations:layouts.organizations.create.rbac.invite')}
            defaultCountry={defaultCountry}
          />
          <IconButton onClick={() => onRemoveAssignment(index)} sx={{ mt: 0.5 }}>
            <DeleteIcon />
          </IconButton>
        </React.Fragment>
      ))}

      <Button
        startIcon={<PlusCircleIcon />}
        sx={{
          marginInlineStart: -1,
          textAlign: 'start',
          justifyContent: 'start',
          alignSelf: 'start',
          ...typography.body3,
          fontWeight: typography.fontWeightMedium,
          '& .MuiButton-icon': { mr: 0.5 }
        }}
        onClick={() => onAddAssignment({ role: '', user: {} as UserIdentifier, phone: '' })}
      >
        {t('organizations:layouts.organizations.create.rbac.add')}
      </Button>
    </Box>
  );
};

export default UserAccessTable;
